import { Button, LoadingDots, ModalDialog, ModalOverlay } from '@flos/react-ui';
import { useRecoilValue } from 'recoil';
import { useHippoText } from '../../hooks/useHippoText';
import { priceTaxAtom } from '../../recoil/atoms/priceTaxAtom';
import { jobAtom } from '../../recoil/atoms/jobAtom';
import { useAsyncRecoilValue } from '../../hooks/useAsyncRecoilValue';

const IncludedPriceModal = ({
    showModal,
    shouldShowPriceIncludingTaxes,
    closeModal,
    isCVR,
    showCarTaxes,
    showTaxesOnlyForLob
}: {
    showModal: boolean;
    shouldShowPriceIncludingTaxes: boolean;
    closeModal: () => void;
    isCVR: boolean;
    showCarTaxes: boolean;
    showTaxesOnlyForLob?: string;
}): React.JSX.Element => {
    const [priceTaxData, isLoading] = useAsyncRecoilValue(priceTaxAtom);
    const jobData = useRecoilValue(jobAtom);

    const governmentFees =
        priceTaxData?.annualTaxes?.filter(
            (tax) =>
                !showTaxesOnlyForLob ||
                !tax.relevantLobs ||
                tax.relevantLobs.length === 0 ||
                tax.relevantLobs.includes(showTaxesOnlyForLob)
        ) ?? [];
    const includedTaxes: string[] = governmentFees
        .filter((chargeCode) => !chargeCode.chargeCode_Top)
        .map((fee) => fee.feeName as string);
    const includedTaxesForCar: string[] = governmentFees
        .filter((chargeCode) => chargeCode.chargeCode_Top)
        .map((fee) => fee.feeName as string);
    const getHippoText = useHippoText();

    const getIncludedInPriceHeader = (isCVR: boolean, paymentFrequency?: string): string => {
        if (paymentFrequency === 'onetime') {
            return getHippoText('DSS-TAX-MODAL--PRICE-SINGLEPAYMENT');
        } else if (isCVR && paymentFrequency === 'annual') {
            return getHippoText('DSS-TAX-MODAL--PRICE-CVR');
        } else if (!isCVR && paymentFrequency === 'annual') {
            return getHippoText('DSS-TAX-MODAL--PRICE');
        } else if (isCVR && paymentFrequency === 'monthly') {
            return getHippoText('DSS-TAX-MODAL--PRICE-MONTHLY-CVR');
        } else {
            return getHippoText('DSS-TAX-MODAL--PRICE-MONTHLY');
        }
    };

    const getNotIncludedInPriceHeader = (isCVR: boolean, paymentFrequency?: string): string => {
        if (paymentFrequency === 'onetime') {
            return getHippoText('DSS-TAX-MODAL-NOT-IN-PRICE-SINGLEPAYMENT');
        } else if (isCVR && paymentFrequency === 'annual') {
            return getHippoText('DSS-TAX-MODAL-NOT-IN-PRICE-CVR');
        } else if (!isCVR && paymentFrequency === 'annual') {
            return getHippoText('DSS-TAX-MODAL-NOT-IN-PRICE');
        } else if (isCVR && paymentFrequency === 'monthly') {
            return getHippoText('DSS-TAX-MODAL-NOT-IN-PRICE-MONTHLY-CVR');
        } else {
            return getHippoText('DSS-TAX-MODAL-NOT-IN-PRICE-MONTHLY');
        }
    };

    const excludedFees = [getHippoText('DSS-TAX-MODAL-NOT-INCLUDED-LIST')];
    const stateTax = includedTaxes.filter((fee) =>
        fee.includes('Skadeforsikringsafgift til staten')
    );
    const feesExcludingStateTax = [...includedTaxes, ...excludedFees].filter(
        (fee) => !fee.includes('Skadeforsikringsafgift til staten')
    );
    const includedInPriceCar = [getHippoText('DSS-TAX-MODAL-INCLUDE-IN-PRICE-CAR')];

    const filteredIncludedFees = shouldShowPriceIncludingTaxes ? includedTaxes : stateTax;
    const includedFeesHeaderTitle = getIncludedInPriceHeader(
        isCVR,
        jobData.selectedPaymentFrequency
    );
    const excludedFeesHeaderTitle = getNotIncludedInPriceHeader(
        isCVR,
        jobData.selectedPaymentFrequency
    );

    const firstHeaderTitle = governmentFees.length
        ? includedFeesHeaderTitle
        : excludedFeesHeaderTitle;

    const includedFeesList = governmentFees.length ? filteredIncludedFees : excludedFees;
    const excludedFeesList = shouldShowPriceIncludingTaxes ? excludedFees : feesExcludingStateTax;

    return (
        <ModalOverlay
            isOpen={showModal}
            onDismiss={() => {
                closeModal();
            }}
            id="modal-dialog"
        >
            <ModalDialog
                closeIconHandler={() => {
                    closeModal();
                }}
            >
                <div className="u-spacing-inset-l">
                    <div className="u-spacing-inset-l--stretch-max">
                        {isLoading ? (
                            <div style={{ position: 'relative' }}>
                                <LoadingDots />
                            </div>
                        ) : (
                            <>
                                <div>
                                    <h4 className="u-spacing-stack-sm">{firstHeaderTitle}</h4>
                                    <ul className="u-spacing-stack-sm">
                                        {includedFeesList.map((fee, id) => (
                                            <li key={id}>{fee}</li>
                                        ))}
                                    </ul>
                                    {!!includedTaxesForCar.length && showCarTaxes && (
                                        <>
                                            <h4 className="u-spacing-stack-sm">
                                                {includedInPriceCar}
                                            </h4>
                                            <ul className="u-spacing-stack-sm">
                                                {includedTaxesForCar.map((fee, id) => (
                                                    <li key={id}>{fee}</li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                    <div className="u-spacing-stack-l" />
                                    {!!governmentFees.length && (
                                        <>
                                            <h4 className="u-spacing-stack-sm">
                                                {excludedFeesHeaderTitle}
                                            </h4>
                                            <ul className="u-spacing-stack-sm">
                                                {excludedFeesList.map((fee, id) => (
                                                    <li key={id}>{fee}</li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                    <div className="u-spacing-stack-l" />
                                </div>
                                <div>
                                    Pris, forsikringssummer og selvrisikobeløb er fastsat i{' '}
                                    {new Date().getFullYear()} og bliver indeksreguleret, som det er
                                    beskrevet i vilkårene.
                                </div>
                            </>
                        )}
                    </div>
                    <Button
                        onClick={() => {
                            closeModal();
                        }}
                        id="included-price-close-button"
                    >
                        Luk
                    </Button>
                </div>
            </ModalDialog>
        </ModalOverlay>
    );
};

export default IncludedPriceModal;
